import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import StatCards from './StatCards';
import ExportButton from './ExportButton';
import BubbleMaps from './BuubleMapChart';
import { formatCurrency, formatNumber } from './ExportUtils';

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  height: '270px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '270px'
}));

const FreightSpendTab = ({
  shipments,
  statCards,
  monthlyData,
  monthlyShipmentTypeData,
  carrierNames,
  carrierSpend,
  carrierMap,
  avgCostPerShipmentData,
  costPerPoundData,
  freightByWeightData,
  freightByMilesData,
  shipTypePieSeries,
  shipTypePieOptionsWithClick,
  setExpandedChart,
  handleMonthFilter,
  handleCarrierFilter,
  handleWeightFilter,
  handleMileFilter,
  weightRangeNames,
  mileRangeNames,
  expandedChart
}) => {
  // Prepare export data
  const getFreightSpendExportData = () => {
    return shipments.map(shipment => ({
      'Shipment ID': shipment.shipmentId || shipment.id,
      Customer: shipment.destinationCompany,
      Origin: `${shipment.originCity}, ${shipment.originState}`,
      Destination: `${shipment.destinationCity}, ${shipment.destinationState}`,
      Carrier: shipment.carrierName,
      'Shipment Type': shipment.shipmentType,
      'Shipment Mode': shipment.shipmentTypeRule,
      'Pickup Date': shipment.actualPickupDate,
      'Delivery Date': shipment.actualDeliveryDate,
      'Freight Spend': shipment.quoteNetCharge,
      'Weight (lbs)': shipment.totalGrossWeight,
      Miles: shipment.shipmentMiles
        ? (shipment.shipmentMiles / 1609.344).toFixed(0)
        : 0,
      Status: shipment.shipmentStatus
    }));
  };

  return (
    <Box>
      <Grid container spacing={0.5} sx={{ mb: 1 }}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={0.5}>
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <StatCards cards={statCards} />
              </ChartContainer>
            </Grid>

            {/* Row 1 */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Monthly Freight Spend
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('monthlyFreight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyData[config.dataPointIndex]?.month;
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : '$' + Math.round(val),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyData.map(item => item.month),
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      tickAmount: 6,
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? '$' + (val / 1000).toFixed(0) + 'K'
                            : '$' + Math.round(val),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#008FFB'],
                    tooltip: {
                      y: {
                        formatter: val =>
                          'Total Freight: $' + val.toLocaleString()
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Freight Spend',
                      data: monthlyData.map(item => Math.round(item.freight))
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Monthly Freight by Shipment Type
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentTypeMonthly')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyShipmentTypeData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : val > 0
                            ? '$' + Math.round(val)
                            : '',
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyShipmentTypeData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? '$' + (val / 1000).toFixed(0) + 'K'
                            : '$' + Math.round(val),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#008FFB', '#00E396', '#FF4560'],
                    legend: { position: 'top', fontSize: '12px' },
                    tooltip: {
                      y: {
                        formatter: (val, { seriesIndex }) => {
                          const type = ['LTL', 'Parcel', 'TL'][seriesIndex];
                          return type + ': $' + val.toLocaleString();
                        }
                      }
                    }
                  }}
                  series={monthlyShipmentTypeData.series}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Freight Spent By Carrier
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('carrierFreight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 280,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedCarrier =
                              carrierNames[config.dataPointIndex];
                            if (selectedCarrier)
                              handleCarrierFilter(selectedCarrier);
                          }
                        }
                      },
                      legend: { show: false },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '95%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const millions = val / 1000000;
                          return (
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0$' +
                            millions.toFixed(1) +
                            'M'
                          );
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 10
                      },
                      xaxis: {
                        min: Math.max(...carrierSpend) * 0.0,
                        max: Math.max(...carrierSpend) * 1.1,
                        labels: {
                          formatter: val =>
                            '$' + (val / 1000000).toFixed(1) + 'M',
                          style: { fontSize: '11px' }
                        }
                      },
                      yaxis: {
                        categories: carrierNames,
                        labels: {
                          show: true,
                          minWidth: 60,
                          maxWidth: 60,
                          style: { fontSize: '11px' }
                        }
                      },
                      grid: { padding: { left: 10, right: 15 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C',
                        '#F86624',
                        '#663399',
                        '#E91E63',
                        '#9C27B0',
                        '#673AB7',
                        '#3F51B5'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex, w }) => {
                            const carrierName = w.config.labels[dataPointIndex];
                            const shipmentCount =
                              carrierMap[carrierName]?.shipmentCount || 0;
                            return `${carrierName}: $${val.toLocaleString()} (${shipmentCount} shipments)`;
                          }
                        }
                      },
                      labels: carrierNames
                    }}
                    series={[{ name: 'Freight Spend', data: carrierSpend }]}
                    type='bar'
                    height={280}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Average Cost per Shipment
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('avgCostPerShipment')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            avgCostPerShipmentData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => '$' + val.toFixed(0),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: avgCostPerShipmentData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val => '$' + (val / 1000).toFixed(1) + 'K',
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#775DD0'],
                    tooltip: {
                      y: {
                        formatter: val =>
                          'Avg Cost: $' + Math.round(val).toLocaleString()
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Avg Cost per Shipment',
                      data: avgCostPerShipmentData.data
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Cost per Pound
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('costPerPound')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            costPerPoundData.categories[config.dataPointIndex];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => '$' + val.toFixed(3),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: costPerPoundData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val => '$' + val.toFixed(1),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#FF4560'],
                    tooltip: {
                      y: {
                        formatter: val => 'Cost per Pound: $' + val.toFixed(2)
                      }
                    }
                  }}
                  series={[
                    { name: 'Cost per Pound', data: costPerPoundData.data }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Freight Spend by Weight (lbs)
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('freightByWeight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 250,
                        toolbar: { show: false },
                        legend: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedWeightRange =
                              weightRangeNames[config.dataPointIndex];
                            if (selectedWeightRange)
                              handleWeightFilter(selectedWeightRange);
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '60%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      legend: { show: false },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          return val >= 1000
                            ? pad + '$' + (val / 1000).toFixed(0) + 'K'
                            : pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 2
                      },
                      xaxis: {
                        labels: {
                          formatter: val =>
                            val >= 1000
                              ? '$' + (val / 1000).toFixed(0) + 'K'
                              : '$' + Math.round(val),
                          style: { fontSize: '11px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '12px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '<500',
                          '500-999',
                          '1,000-1,999',
                          '2,000-4,999',
                          '5,000-9,999',
                          '10,000-19,999',
                          '20,000-40,000',
                          '>40,000'
                        ],
                        labels: {
                          show: true,
                          minWidth: 100,
                          maxWidth: 120,
                          style: { fontSize: '11px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: -5, right: 15 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex }) => {
                            const weightRanges = [
                              '<500',
                              '500-999',
                              '1,000-1,999',
                              '2,000-4,999',
                              '5,000-9,999',
                              '10,000-19,999',
                              '20,000-40,000',
                              '>40,000'
                            ];
                            return `${weightRanges[dataPointIndex]}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '<500',
                        '500-999',
                        '1,000-1,999',
                        '2,000-4,999',
                        '5,000-9,999',
                        '10,000-19,999',
                        '20,000-40,000',
                        '>40,000'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByWeightData }
                    ]}
                    type='bar'
                    height={250}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Freight Spend by Miles
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('freightByMiles')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 280,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMileRange =
                              mileRangeNames[config.dataPointIndex];
                            if (selectedMileRange)
                              handleMileFilter(selectedMileRange);
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '60%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      legend: { show: false },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          return val >= 1000
                            ? pad + '$' + (val / 1000).toFixed(0) + 'K'
                            : pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: val =>
                            val >= 1000
                              ? '$' + (val / 1000).toFixed(0) + 'K'
                              : '$' + Math.round(val),
                          style: { fontSize: '11px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '12px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '0-49',
                          '50-99',
                          '100-249',
                          '250-499',
                          '500-749',
                          '750-999',
                          '1,000-1,499',
                          '1,500-1,999',
                          '2,000-2,499',
                          '2,500+'
                        ],
                        labels: {
                          show: true,
                          minWidth: 80,
                          maxWidth: 100,
                          style: { fontSize: '11px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: 5, right: 5 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex }) => {
                            const mileRanges = [
                              '0-49 miles',
                              '50-99 miles',
                              '100-249 miles',
                              '250-499 miles',
                              '500-749 miles',
                              '750-999 miles',
                              '1,000-1,499 miles',
                              '1,500-1,999 miles',
                              '2,000-2,499 miles',
                              '2,500+ miles'
                            ];
                            return `${mileRanges[dataPointIndex]}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '0-49',
                        '50-99',
                        '100-249',
                        '250-499',
                        '500-749',
                        '750-999',
                        '1,000-1,499',
                        '1,500-1,999',
                        '2,000-2,499',
                        '2,500+'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByMilesData }
                    ]}
                    type='bar'
                    height={280}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Freight Spend by Shipment Type
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentTypePie')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={shipTypePieOptionsWithClick}
                  series={shipTypePieSeries}
                  type='donut'
                  height={220}
                />
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <BubbleMaps
            shipments={shipments}
            setExpandedChart={setExpandedChart}
            expandedChart={expandedChart}
            tabValue={0}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FreightSpendTab;
